'use client';

import {memo, ReactNode, useEffect, useRef, useState} from 'react';
import {cn} from '@tinct/core-utils';
import {
  Button,
  Stepper,
  StepperIndicator,
  StepperItem,
  StepperNav,
  StepperSeparator, StepperTitle,
  StepperTrigger,
  TinctLogo,
} from '@tinct/web-components';
import {Check, X} from 'lucide-react';
import {useFunnel} from '../store/funnel-provider';
import {FunnelButton} from './funnel-button';
import {Link, useNavigate} from 'react-router-dom';

export interface FunnelTopBarProps {
  /**
   * Additional class name for the top bar container
   */
  className?: string;
  /**
   * Whether to show the close button
   * @default true
   */
  showCloseButton?: boolean;
  /**
   * Custom component to render in the left section (overrides step's optionComponent)
   */
  leftComponent?: ReactNode;
}

/**
 * FunnelTopBar component that combines:
 * - Left: Logo or custom optionComponent from current step
 * - Center: Horizontal stepper navigation (uses Stepper primitives)
 * - Right: Action buttons from current step + close button
 */
export const FunnelTopBar = memo(
  ({className, showCloseButton = true, leftComponent}: FunnelTopBarProps) => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isCompact, setIsCompact] = useState(false);
    const rootRef = useRef<HTMLDivElement>(null);
    const leftRef = useRef<HTMLDivElement>(null);
    const rightRef = useRef<HTMLDivElement>(null);
    const stepperRef = useRef<HTMLDivElement>(null);
    const navigate = useNavigate();

    useEffect(() => {
      if (typeof window === 'undefined') return;

      const handleScroll = () => {
        setIsScrolled(window.scrollY > 0);
      };

      handleScroll();

      window.addEventListener('scroll', handleScroll, {passive: true});
      return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const context = useFunnel<unknown>();
    const {
      stepperSteps: steps,
      stepperActiveStepIndex,
      showStepper,
      activeStep,
      getActiveStepActions,
      state,
      exitUrl,
    } = context;

    const stepperNaturalWidthRef = useRef(0);

    useEffect(() => {
      const root = rootRef.current;
      const left = leftRef.current;
      const right = rightRef.current;
      const stepperContainer = stepperRef.current;
      if (!root || !left || !right || !stepperContainer) return;

      // Capture stepper's natural content width on first render (non-compact)
      if (stepperNaturalWidthRef.current === 0) {
        const stepper = stepperContainer.querySelector('[data-slot="stepper"]') as HTMLElement;
        if (stepper) {
          stepperNaturalWidthRef.current = stepper.scrollWidth;
        }
      }

      const observer = new ResizeObserver(() => {
        const rootWidth = root.clientWidth;
        const leftWidth = left.offsetWidth;
        const rightWidth = right.offsetWidth;
        const padding = 32; // px-4 on root (16px each side)
        const gaps = 32; // mx-4 on stepper container (16px each side)

        const availableForStepper = rootWidth - leftWidth - rightWidth - padding - gaps;

        setIsCompact(availableForStepper < stepperNaturalWidthRef.current);
      });

      observer.observe(root);
      return () => {
        observer.disconnect();
        stepperNaturalWidthRef.current = 0;
      };
    }, [steps.length]);

    const actions = getActiveStepActions();

    const defaultLeftSection = <TinctLogo />;

    const leftSection =
      leftComponent ?? activeStep?.optionComponent ?? defaultLeftSection;

    return (
      <div
        ref={rootRef}
        className={cn(
          'bg-background flex h-16 items-center px-4 relative w-full transition-shadow fixed top-0 z-10',
          isScrolled && 'shadow-sm',
          className
        )}
      >
        {/* Left section - Logo or optionComponent */}
        <div ref={leftRef} className="flex items-center shrink-0">{leftSection}</div>

        {/* Center section - Stepper */}
        {showStepper && steps.length > 0 ? (
          <div ref={stepperRef} className="flex-1 min-w-0 mx-4 h-full flex items-center justify-center overflow-hidden">
            <Stepper
              className={cn({
                "w-auto": !isCompact,
                "w-full": isCompact
              })}
              value={stepperActiveStepIndex + 1}
              onValueChange={(value) => {
                const targetStep = steps[value - 1];
                if (targetStep?.isAvailable && targetStep.navigateTo) {
                  navigate(targetStep.navigateTo);
                }
              }}
              indicators={{
                completed: (
                  <Check className="size-3 text-primary" strokeWidth={2.5} />
                ),
              }}
            >
              <StepperNav>
                {steps.map((step, index) => (
                  <StepperItem
                    key={index}
                    step={index + 1}
                    disabled={!step.isAvailable}
                    className={cn(
                      isCompact && 'flex-1 relative items-start'
                    )}
                    style={!isCompact ? { minWidth: `calc(${step.label.length}ch + 24px)` } : undefined}
                  >
                    <StepperTrigger
                      className={cn(
                        isCompact && 'flex-col'
                      )}
                    >
                      <StepperIndicator>{index + 1}</StepperIndicator>
                      <StepperTitle
                        className={cn(
                          'font-medium text-xs px-1',
                          isCompact
                            ? 'truncate w-full text-center'
                            : 'whitespace-nowrap',
                          index <= stepperActiveStepIndex
                            ? 'text-foreground'
                            : 'text-muted-foreground'
                        )}>{step.label}</StepperTitle>
                    </StepperTrigger>
                    {index < steps.length - 1 && (
                      <StepperSeparator className={cn(
                        {
                          "absolute top-2.5 left-[calc(50%+1.5rem)] m-0 w-[calc(100%-3rem)] flex-none": isCompact,
                          "md:mx-2.5": !isCompact
                        }
                      )} />
                    )}
                  </StepperItem>
                ))}
              </StepperNav>
            </Stepper>
          </div>
        ): <div className={'flex-1'}></div>}

        {/* Right section - Actions + Close button */}
        <div ref={rightRef} className="flex items-center gap-4 shrink-0">
          {/* Action buttons */}
          <div className="flex items-center gap-2">
            {actions.map((action) =>
              typeof action.label === 'string' ? (
                <FunnelButton
                  key={action.id}
                  variant={action.variant}
                  className="h-9 px-4 text-sm font-medium"
                  onClick={() => {
                    action.onClick?.(context, state);
                  }}
                >
                  {action.label || ''}
                </FunnelButton>
              ) : (
                <div key={action.id}>{action.label}</div>
              )
            )}
          </div>

          {/* Close button */}
          {showCloseButton && exitUrl && (
            <Link to={exitUrl}>
              <Button
                variant="outline"
                size="icon"
                className="size-9 shadow-xs"
              >
                <X className="size-4 opacity-70" />
              </Button>
            </Link>
          )}
        </div>
      </div>
    );
  }
);

FunnelTopBar.displayName = 'FunnelTopBar';
